/**
 * Match consensus multiplier from community outcome-hit rate (lock-time pick stats + 90-min result).
 * Higher multiplier when fewer players called the correct 1×2.
 * Scales outcome points only (precision bonus is flat). Players who voted for a community top-3
 * match get an additive +0.1 on that match only.
 */

/** @type {ReadonlyArray<{ minPercent: number, multiplier: number }>} */
const CONSENSUS_MULTIPLIER_TIERS = [
  { minPercent: 85, multiplier: 1.0 },
  { minPercent: 75, multiplier: 1.1 },
  { minPercent: 65, multiplier: 1.2 },
  { minPercent: 55, multiplier: 1.3 },
  { minPercent: 45, multiplier: 1.4 },
  { minPercent: 35, multiplier: 1.5 },
  { minPercent: 25, multiplier: 1.6 },
  { minPercent: 15, multiplier: 1.7 },
  { minPercent: 10, multiplier: 1.8 },
  { minPercent: 5, multiplier: 1.9 },
  { minPercent: 0, multiplier: 2.0 }
];

/** Additive boost for players who voted for a community top-3 match (e.g. 1.2 → 1.3). */
const VOTE_BOOST_ADD = 0.1;

function getMatchOutcome(homeScore, awayScore) {
  const h = Number(homeScore);
  const a = Number(awayScore);
  if (!Number.isFinite(h) || !Number.isFinite(a)) return null;
  if (h > a) return 'home';
  if (a > h) return 'away';
  return 'draw';
}

/**
 * @returns {{ outcomeHits: number, resultHits: number, totalBets: number }}
 */
function livePickCounts(pickStats, homeScore, awayScore) {
  const totalBets = Number(pickStats?.totalBets) || 0;
  if (!totalBets) return { outcomeHits: 0, resultHits: 0, totalBets: 0 };

  const outcome = getMatchOutcome(homeScore, awayScore);
  const outcomeHits =
    outcome && pickStats?.betsOutcome ? Number(pickStats.betsOutcome[outcome]) || 0 : 0;

  const resultKey = `${Number(homeScore)}-${Number(awayScore)}`;
  const resultHits =
    pickStats?.betsResults && pickStats.betsResults[resultKey] != null
      ? Number(pickStats.betsResults[resultKey]) || 0
      : 0;

  return { outcomeHits, resultHits, totalBets };
}

function correctOutcomePercent(outcomeHits, totalBets) {
  if (!totalBets) return 0;
  return Math.round((Number(outcomeHits) / Number(totalBets)) * 100);
}

function consensusMultiplierForPercent(percent) {
  const pct = Number(percent);
  if (!Number.isFinite(pct)) return 1;
  for (const tier of CONSENSUS_MULTIPLIER_TIERS) {
    if (pct >= tier.minPercent) return tier.multiplier;
  }
  return 2.0;
}

function isVoteBoosted(pickStats) {
  return pickStats?.voteBoosted === true || pickStats?.vb === 1;
}

/**
 * Add vote boost when the match is community-boosted and this player voted for it (1.2 → 1.3).
 * @param {number} multiplier
 * @param {object} pickStats
 * @param {boolean} [playerVotedForMatch=false]
 */
function applyVoteBoost(multiplier, pickStats, playerVotedForMatch = false) {
  const m = Number(multiplier);
  if (!Number.isFinite(m)) return 1;
  if (!playerVotedForMatch || !isVoteBoosted(pickStats)) return m;
  return Math.round((m + VOTE_BOOST_ADD) * 10) / 10;
}

/**
 * @param {object} pickStats
 * @param {number} homeScore
 * @param {number} awayScore
 * @param {boolean} [playerVotedForMatch=false] — only then apply +0.1 on vote-boosted matches
 */
function consensusMultiplierFromPickStats(pickStats, homeScore, awayScore, playerVotedForMatch = false) {
  const { outcomeHits, totalBets } = livePickCounts(pickStats, homeScore, awayScore);
  if (!totalBets) return applyVoteBoost(1, pickStats, playerVotedForMatch);
  const base = consensusMultiplierForPercent(correctOutcomePercent(outcomeHits, totalBets));
  return applyVoteBoost(base, pickStats, playerVotedForMatch);
}

function applyConsensusMultiplier(points, multiplier) {
  const m = Number(multiplier);
  if (!Number.isFinite(m) || m === 1) return Number(points) || 0;
  return Math.round((Number(points) || 0) * m);
}

/**
 * Rarity scales outcome points (plus optional extraScaled, e.g. legacy Tip the Scales bump).
 * Precision bonus and other flats are added after — not multiplied.
 * @param {number} outcomePts
 * @param {number} precisionBonus
 * @param {number} multiplier
 * @param {number} [extraScaled=0]
 * @param {number} [flatExtra=0]
 */
function applyConsensusToLadderParts(
  outcomePts,
  precisionBonus,
  multiplier,
  extraScaled = 0,
  flatExtra = 0
) {
  const scaled = applyConsensusMultiplier(
    (Number(outcomePts) || 0) + (Number(extraScaled) || 0),
    multiplier
  );
  return scaled + (Number(precisionBonus) || 0) + (Number(flatExtra) || 0);
}

function formatConsensusMultiplierLabel(multiplier) {
  const m = Number(multiplier);
  if (!Number.isFinite(m) || m === 1) return '1×';
  if (Number.isInteger(m)) return `${m}×`;
  return `${m.toFixed(1)}×`;
}

module.exports = {
  CONSENSUS_MULTIPLIER_TIERS,
  VOTE_BOOST_ADD,
  getMatchOutcome,
  livePickCounts,
  correctOutcomePercent,
  consensusMultiplierForPercent,
  isVoteBoosted,
  applyVoteBoost,
  consensusMultiplierFromPickStats,
  applyConsensusMultiplier,
  applyConsensusToLadderParts,
  formatConsensusMultiplierLabel
};
